import { Button } from '~/components/primitives/Button'
import { InfoButton } from '~/components/primitives/InfoButton'
import type { BackupReminder as Reminder } from '~/services/backupReminderService'
import styles from './BackupReminder.module.css'

/**
 * The backup reminder (D-084).
 *
 * **Neutral, dismissible, and never blocking.** The quality bar forbids streak
 * mechanics and punitive missed-entry language, and a backup reminder is where
 * that is easiest to violate by accident — "you haven't backed up in 3 weeks!"
 * writes itself. This states the date, says what is at risk in plain words, and
 * offers a way to act or to leave.
 *
 * It reports **days**, never a count of missed backups, and never a colour on
 * its own: the words carry the meaning.
 *
 * **The state stays visible and the reasoning moved** (C7.4 phase C). The line
 * that says where the data actually is, is one sentence now; what is at risk and
 * why is behind the information control. **Its action is secondary**, because
 * Today has one primary action and it is the day's own — a notice that arrives
 * uninvited must not outrank the thing the user opened the app to do.
 */

interface BackupReminderProps {
  reminder: Reminder
  onBackUp: () => void
  onDismiss: () => void
  busy: boolean
}

export function BackupReminder({ reminder, onBackUp, onDismiss, busy }: BackupReminderProps) {
  if (reminder.kind === 'none') return null

  return (
    <section class={styles.card} aria-labelledby="backup-reminder-heading">
      <h2 id="backup-reminder-heading" class={styles.heading}>
        {reminder.kind === 'never' ? 'No backup yet' : 'Time for a backup'}
      </h2>

      <div class={styles.line}>
        <p class={styles.body}>
          {reminder.kind === 'never'
            ? 'Everything is on this device only.'
            : `Your last backup was ${describeDays(reminder.daysAgo)}.`}
        </p>
        <InfoButton topic="keeping a copy" title="Why a backup matters">
          <p>
            Project Sola keeps everything on this device. If you lose the phone, clear the browser
            data, or the storage is reclaimed, there is no copy to go back to.
          </p>
          <p>
            A backup is a single file you save wherever you keep files. It is not encrypted, and
            Settings says so again before the file is made.
          </p>
        </InfoButton>
      </div>

      <div class={styles.actions}>
        <Button variant="secondary" onClick={onBackUp} busy={busy}>
          Back up now
        </Button>
        <Button variant="secondary" onClick={onDismiss}>
          Not now
        </Button>
      </div>
    </section>
  )
}

/** Plain days. No streak, no tally, no exclamation. */
function describeDays(days: number): string {
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  return `${days} days ago`
}
